import { useCallback } from "react";
import { api } from "../services/api/api";
import { useWorkorderContext } from "../context/WorkorderContext";
import { t } from "i18next";

/**
 * Hook pour gérer les machines dans un workorder
 * @param workorderId - ID du workorder
 */
export function useWorkorderMachine(workorderId: string) {
  // Utiliser le contexte pour accéder aux méthodes de mise à jour
  const {
    addMachine: contextAddMachine,
    updateMachine: contextUpdateMachine,
    removeMachine: contextRemoveMachine,
    getMachines,
  } = useWorkorderContext();

  /**
   * Ajoute une machine au workorder
   * @param machineData - Données de la machine à ajouter
   */
  const addMachine = useCallback(
    async (machineData: { machine: { id: string }; notes: string }) => {
      try {
        // Vérifier si la machine existe déjà dans le workorder
        const existingMachines = getMachines();
        const machineExists = existingMachines.some(
          (m) => m.machine?.id === machineData.machine.id
        );

        if (machineExists) {
          const errorMsg = t("workorder.machines.errors.machineAlreadyExists");
          console.warn(errorMsg);
          throw new Error(errorMsg);
        }

        // Appel API pour ajouter la machine avec le format correct
        const response = await api.post(
          `users/workorders/${workorderId}/machine`,
          machineData
        );

        // Mettre à jour le contexte
        if (response) {
          contextAddMachine(response as EasyTech.WorkorderMachine);
        }

        return response;
      } catch (error) {
        console.error(t("workorder.machines.errors.addMachine"), error);
        throw error;
      }
    },
    [workorderId, contextAddMachine, getMachines]
  );

  /**
   * Met à jour une machine dans le workorder
   * @param machineId - ID de la machine à mettre à jour
   * @param machineData - Nouvelles données de la machine
   */
  const updateMachine = useCallback(
    async (
      woMachineId: string, 
      data: { machine: { id: string }; notes: string }
    ) => {
      try {
        // Obtenir la machine actuelle
        const existingMachines = getMachines();
        const currentMachine = existingMachines.find(m => m.id === woMachineId);
        
        if (currentMachine && currentMachine.machine.id !== data.machine.id) {
          // Vérifier si la nouvelle machine existe déjà dans le workorder
          const machineExists = existingMachines.some(
            (m) => m.id !== woMachineId && m.machine?.id === data.machine.id
          );
          
          if (machineExists) {
            const errorMsg = t("workorder.machines.errors.machineAlreadyExists");
            console.warn(errorMsg);
            throw new Error(errorMsg);
          }
        }

        // Appel API pour mettre à jour la machine avec le format correct
        const response = await api.put(`users/workorders/${workorderId}/machine/${woMachineId}`, data);

        // Mettre à jour le contexte
        if (response) {
          const updatedMachine = {
            ...response as EasyTech.WorkorderMachine,
            machine: (response as EasyTech.WorkorderMachine).machine || data.machine,
            notes: (response as EasyTech.WorkorderMachine).notes !== undefined 
              ? (response as EasyTech.WorkorderMachine).notes 
              : data.notes
          };
          
          contextUpdateMachine(woMachineId, updatedMachine);
        }

        return response;
      } catch (error) {
        console.error(t("workorder.machines.errors.updateMachine"), error);
        throw error;
      }
    },
    [workorderId, contextUpdateMachine, getMachines]
  );

  /**
   * Supprime une machine du workorder
   * @param machineId - ID de la machine à supprimer
   */
  const removeMachine = useCallback(
    async (woMachineId: string) => {
      try {
        // Appel API pour supprimer la machine d'un workorder
        await api.delete(`users/workorders/${workorderId}/machine/${woMachineId}`);

        // Mettre à jour le contexte
        contextRemoveMachine(woMachineId);

        return true;
      } catch (error) {
        console.error(t("workorder.machines.errors.removeMachine"), error);
        throw error;
      }
    },
    [workorderId, contextRemoveMachine]
  );

  return {
    addMachine,
    updateMachine,
    removeMachine,
  };
}
